import React, { useEffect, useState } from 'react';

// Черновик правки — в sessionStorage по id записи: переход к другой записи в
// боковой панели пересоздаёт экран, и без этого правка пропадала бы молча.
const draftKey = (id) => `newton.transcript-draft:${id}`;

// Рядом с черновиком — текст, от которого он начат (base): если запись с тех пор
// поменялась (догоняющий PATCH после распознавания, переименование на другом
// устройстве), редактор предупредит, что сохранение затрёт новое.
export function loadDraft(id) {
  if (!id) return null;
  try {
    const raw = window.sessionStorage.getItem(draftKey(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.draft === 'string' ? parsed : null;
  } catch (_) { return null; }
}

function storeDraft(id, value) {
  if (!id) return;
  try {
    if (value === null) window.sessionStorage.removeItem(draftKey(id));
    else window.sessionStorage.setItem(draftKey(id), JSON.stringify(value));
  } catch (_) { /* приватный режим или полное хранилище — живём без черновика */ }
}

/**
 * Правка текста расшифровки в записи Истории. Сохранение уходит с
 * edit_kind: 'manual' — сервер кладёт правку в журнал (bit-history/app/edits.py),
 * из которого потом вырастут личный словарь замен и замер качества распознавания.
 */
export default function TranscriptEditor({ itemId, initialText, onSave, onCancel, saving, error }) {
  const [saved] = useState(() => loadDraft(itemId));
  const [draft, setDraft] = useState(() => saved?.draft ?? (initialText || ''));
  const base = saved?.base ?? (initialText || '');
  const stale = !!saved && saved.base !== (initialText || '');
  const dirty = draft !== (initialText || '');

  // Черновик пишется с задержкой: двухчасовая расшифровка на каждое нажатие
  // клавиши в sessionStorage заметно тормозит телефон.
  useEffect(() => {
    const t = setTimeout(() => storeDraft(itemId, dirty ? { base, draft } : null), 400);
    return () => clearTimeout(t);
  }, [itemId, draft, dirty, base]);

  // Закрыть вкладку с несохранённой правкой — браузер переспросит.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const cancel = () => {
    if (dirty && !window.confirm('Правка не сохранена. Выйти без сохранения?')) return;
    storeDraft(itemId, null);
    onCancel();
  };

  // onSave отвечает, сохранилось ли: только тогда черновик больше не нужен.
  // Поле на время сохранения заблокировано — дописанное после нажатия не пропадёт.
  const save = async () => {
    if (await onSave(draft)) storeDraft(itemId, null);
  };

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex items-center justify-between gap-2 border-b border-paper-line py-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3">Правка текста</span>
        <div className="flex items-center gap-2">
          <button type="button" onClick={cancel}
                  className="rounded-[8px] bg-paper-bg px-[10px] py-[5px] text-[12px] text-paper-ink hover:bg-paper-panel">
            Отмена
          </button>
          <button type="button" onClick={save} disabled={!dirty || saving || !draft.trim()}
                  className="rounded-[8px] bg-paper-accent px-[10px] py-[5px] text-[12px] font-semibold text-white
                             hover:bg-paper-accent-hover disabled:opacity-50">
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </div>
      {stale && (
        <div className="text-[13px] text-paper-warning">
          Пока вы правили, текст записи изменился. «Сохранить» заменит его вашей версией, «Отмена» — вернёт текущий.
        </div>
      )}
      {error && <div className="text-[13px] text-destructive">{error}</div>}
      <textarea
        aria-label="Текст расшифровки"
        value={draft}
        readOnly={saving}
        onChange={(e) => setDraft(e.target.value)}
        spellCheck
        className="min-h-[50vh] flex-1 resize-none rounded-panel bg-paper-panel p-4 font-montserrat text-sm
                   leading-relaxed text-paper-ink outline-none focus:ring-1 focus:ring-paper-accent"
      />
    </div>
  );
}
